import React, { useCallback } from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import styled from '@emotion/styled';

import { useFirebaseAuth } from '../../hooks';

/**
 * Navigation items got a field named `visibility` which is a multiselect
 * field. Visibility field could return 3 possible outcomes.
 *
 * - auth & public - doesn't if user is logged in or not.
 * Item should be displayed.
 * - public - should be displayed only if user is not logged in.
 * - auth - should be displayed only if user is logged in.
 * @type {React.FC<NavigationProps>} */

export const Navigation: React.FC<any> = ({ items, ...props }) => {
    const { currentUser, handleSignOutCurrentUser } = useFirebaseAuth();

    const { pathname } = useLocation();
    const navigate = useNavigate();

    const handleOnClick = useCallback(
        (
            event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
            onClickMethod: 'logout',
        ) => {
            event.preventDefault();

            if (onClickMethod === 'logout') {
                sessionStorage.clear();
                handleSignOutCurrentUser();
                navigate('/');
            }
        },
        [handleSignOutCurrentUser, navigate],
    );

    return (
        <List {...props}>
            {items.map(
                ({ _uid, label, icon, url, visibility, onClick }: any) => {
                    const isPublic = visibility.includes('public');
                    const isAuth = visibility.includes('auth');

                    const bothPublicAndAuth = isPublic && isAuth;
                    const onlyPublic = isPublic && !isAuth && !currentUser;
                    const onlyAuth = isAuth && !isPublic && currentUser;

                    return (
                        (bothPublicAndAuth || onlyPublic || onlyAuth) && (
                            <li key={_uid} className="NavigationLinks__link">
                                <Link
                                    to={url}
                                    data-event={`navigation link (${label})`}
                                    onClick={
                                        onClick
                                            ? (event) =>
                                                  handleOnClick(event, onClick)
                                            : undefined
                                    }
                                    {...(pathname === url && {
                                        style: {
                                            pointerEvents: 'none',
                                        },
                                        className: 'active',
                                    })}
                                >
                                    {icon && icon.filename ? (
                                        <div
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: '18px',
                                                height: '18px',
                                            }}
                                        >
                                            <img
                                                src={icon.filename}
                                                style={{
                                                    display: 'block',
                                                    maxWidth: '100%',
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        label
                                    )}
                                </Link>
                            </li>
                        )
                    );
                },
            )}
        </List>
    );
};

const List = styled.ul`
    font-family: ${(props) => props.theme.headings.fontFamily};
    list-style: none;
    padding: 0;
    margin: 0;

    a {
        color: inherit;
        text-decoration: none;
    }
`;
